<script lang="ts">
import { tv } from 'tailwind-variants';

const controls = tv({
	slots: {
		root: 'block',
		controlBar: 'flex w-full items-center gap-1.5',
		control: 'rounded-sm p-1',
		controlIcon: 'size-4',
		text: 'font-feature-calt text-xs tabular-nums',
		timeRange: 'm-2 block h-1 min-h-0 rounded-md p-0',
	},
	variants: {
		audio: {
			true: {
				control: 'text-highlighted hover:bg-elevated',
				text: 'text-highlighted',
				timeRange: `
					bg-border [--media-range-bar-color:var(--ui-text)] [--media-range-thumb-background:var(--ui-text-highlighted)]
					[--media-time-range-buffered-color:var(--ui-border-accented)]
				`,
			},
			false: {
				control: 'text-white hover:bg-(--ui-color-neutral-600)',
				controlBar: `
					m-2 w-[calc(100%-1rem)] rounded-sm bg-(--ui-color-neutral-700)/50 p-1 backdrop-blur-xl backdrop-saturate-180
				`,
				timeRange: `
					bg-white/25 [--media-range-bar-color:#fff] [--media-range-thumb-background:#fff]
					[--media-time-range-buffered-color:rgba(255,255,255,0.25)]
				`,
			},
		},
	},
});
</script>

<script setup lang="ts">
import { computed } from 'vue';
import { useAppConfig } from '#app/config';

import 'media-chrome';

const { audio } = defineProps<{
	audio?: boolean;
	download?: boolean;
	src?: string;
}>();

const appConfig = useAppConfig();

const ui = computed(() => controls({
	audio,
}));

const customize = (content: string) => {
	return content
		.replaceAll(/fill="[^"]*"/g, 'fill="currentColor"')
		.replaceAll(/stroke="[^"]*"/g, 'stroke="none"');
};
</script>

<!-- eslint-disable vue/no-deprecated-slot-attribute -->

<template>
	<media-controller
		:audio
		:class="ui.root()"
		style="
		--media-font: var(--font-sans);
		--media-background-color: transparent;
    --media-control-background: transparent;
    --media-control-hover-background: transparent;
  "
	>
		<slot />
		<slot name="media" v-bind="{ slot: 'media' }" />
		<media-loading-indicator v-if="!audio" slot="centered-chrome" />
		<media-control-bar :class="ui.controlBar()">
			<media-play-button :class="ui.control()" notooltip>
				<DIcon slot="play" name="lucide:play" :class="ui.controlIcon()" mode="svg" :customize />
				<DIcon slot="pause" name="lucide:pause" :class="ui.controlIcon()" mode="svg" :customize />
			</media-play-button>
			<media-mute-button :class="ui.control()" notooltip>
				<div slot="off" class="relative" :class="ui.controlIcon()">
					<DIcon name="lucide:volume-x" class="absolute inset-0 size-4" />
					<DIcon name="lucide:volume" class="absolute inset-0 size-4" mode="svg" :customize />
				</div>
				<div slot="high" class="relative" :class="ui.controlIcon()">
					<DIcon name="lucide:volume-2" class="absolute inset-0 size-4" />
					<DIcon name="lucide:volume" class="absolute inset-0 size-4" mode="svg" :customize />
				</div>
			</media-mute-button>
			<media-time-display :class="ui.text()" />
			<media-time-range
				:class="ui.timeRange()"
				style="
        --media-range-track-background: transparent;
        --media-range-track-border-radius: 0.25rem;
        --media-range-track-height: 0.25rem;
        --media-range-thumb-width: 0.125rem;
        --media-range-thumb-height: 0.75rem;
        --media-preview-time-text-shadow: transparent;
      "
			/>
			<media-duration-display :class="ui.text()" />
			<media-playback-rate-button
				class="select-none"
				:class="[ui.control(), ui.text()]"
				notooltip
			/>
			<DTooltip v-if="download && src" :text="$t('common.download')">
				<DButton
					:icon="appConfig.ui.icons.download"
					:class="ui.control()"
					:href="src"
					variant="ghost"
					color="neutral"
					size="xs"
					download
					target="_blank"
					rel="noopener noreferrer"
				/>
			</DTooltip>
			<media-pip-button v-if="audio === false" class="[[mediapipunavailable]]:hidden" :class="ui.control()" notooltip>
				<DIcon slot="enter" name="lucide:picture-in-picture-2" :class="ui.controlIcon()" />
				<DIcon slot="exit" name="lucide:picture-in-picture" :class="ui.controlIcon()" />
			</media-pip-button>
			<media-fullscreen-button v-if="audio === false" class="[[mediafullscreenunavailable]]:hidden" :class="ui.control()" notooltip>
				<DIcon slot="enter" :name="appConfig.ui.icons.enterFullscreen" :class="ui.controlIcon()" />
				<DIcon slot="exit" :name="appConfig.ui.icons.exitFullscreen" :class="ui.controlIcon()" />
			</media-fullscreen-button>
		</media-control-bar>
	</media-controller>
</template>
